import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Box, Fade, SxProps } from '@mui/material';
import { Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';

import ActionButton from '~components/buttons/actionButton';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import { StatusVariant } from '~enums';
import Page from '~interfaces/page';
import { Dimens } from '~themes';

interface Props {
	currentPage: number;
	onPageChanged: (newPage: number) => void;
	onFinish: () => void;
	isLoading: boolean;
	pages: Page[];
	sx?: SxProps;
}

const Pager = ({ currentPage, onPageChanged, onFinish, isLoading = false, pages, sx }: Props) => {
	const { t } = useTranslation();
	const [pageHeight, setPageHeight] = useState(0);
	const activePageRef = useRef<HTMLDivElement | null>(null);
	const fromPageRef = useRef<HTMLDivElement | null>(null);
	const toPageRef = useRef<HTMLDivElement | null>(null);
	const [transition, setTransition] = useState<{
		fromIndex: number;
		toIndex: number;
		direction: 1 | -1;
		phase: 'prepare' | 'animate';
	} | null>(null);
	const isAnimating = transition !== null;
	const uiPageIndex = transition?.toIndex ?? currentPage;

	useLayoutEffect(() => {
		if (transition) return;
		if (!open) return;
		const el = activePageRef.current;
		if (!el) return;

		let rafId = 0;
		const updateSize = () => {
			const rect = el.getBoundingClientRect();
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setPageHeight(rect.height);
		};

		updateSize();
		const observer = new ResizeObserver(() => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(updateSize);
		});
		observer.observe(el);

		return () => {
			cancelAnimationFrame(rafId);
			observer.disconnect();
		};
	}, [open, currentPage, pages.length, transition]);

	useLayoutEffect(() => {
		if (!transition || transition.phase !== 'prepare') return;
		const fromEl = fromPageRef.current;
		const toEl = toPageRef.current;
		if (!fromEl || !toEl) return;

		const fromRect = fromEl.getBoundingClientRect();
		const toRect = toEl.getBoundingClientRect();

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setPageHeight(fromRect.height);

		let raf1 = 0;
		let raf2 = 0;
		raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(() => {
				setTransition((current) => {
					if (!current || current.phase !== 'prepare') return current;
					return {
						...current,
						phase: 'animate',
					};
				});
				setPageHeight(toRect.height);
			});
		});

		return () => {
			cancelAnimationFrame(raf1);
			cancelAnimationFrame(raf2);
		};
	}, [transition]);

	const goTo = (nextIndex: number) => {
		if (isAnimating) return;

		if (nextIndex < 0) {
			return;
		}

		if (nextIndex >= pages.length) {
			onFinish();
			return;
		}

		if (nextIndex === currentPage) return;
		setTransition({
			fromIndex: currentPage,
			toIndex: nextIndex,
			direction: nextIndex > currentPage ? 1 : -1,
			phase: 'prepare',
		});
	};

	const handleSlideTransitionEnd = (propertyName: string) => {
		if (propertyName !== 'transform') return;
		if (!transition || transition.phase !== 'animate') return;

		onPageChanged(transition.toIndex);
		setTransition(null);
	};

	useEffect(() => {
		if (currentPage >= pages.length) {
			onPageChanged(Math.max(0, pages.length - 1));
		}
	}, [pages.length, currentPage]);

	const isFirstPage = uiPageIndex === 0;
	const isLastPage = uiPageIndex === pages.length - 1;
	const abortButtonText = t('previous', { ns: 'actions' });
	const confirmButtonText =
		isLastPage ? t('confirm', { ns: 'actions' }) : t('next', { ns: 'actions' });

	return (
		<Box sx={sx}>
			<Box
				sx={{
					width: '100%',
					height: pageHeight ? Dimens.fixed(pageHeight) : 'auto',
					transition: 'width 220ms ease, height 220ms ease',
					willChange: 'width, height',
					overflow: 'hidden',
				}}
			>
				{transition ?
					<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								transform:
									transition.phase === 'animate' ?
										`translateX(${-transition.direction * 100}%)`
									:	'translateX(0%)',
								transition: 'transform 220ms ease',
								willChange: 'transform',
							}}
						>
							<Box
								ref={fromPageRef}
								sx={{
									boxSizing: 'border-box',
									width: '100%',
									height: 'fit-content',
								}}
							>
								{pages[transition.fromIndex]?.content}
							</Box>
						</Box>
						<Box
							onTransitionEnd={(event) => {
								handleSlideTransitionEnd(event.propertyName);
							}}
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								transform:
									transition.phase === 'animate' ?
										'translateX(0%)'
									:	`translateX(${transition.direction * 100}%)`,
								transition: 'transform 220ms ease',
								willChange: 'transform',
							}}
						>
							<Box ref={toPageRef}>{pages[transition.toIndex]?.content}</Box>
						</Box>
					</Box>
				:	<Box
						key={pages[currentPage]?.key ?? 'empty-page'}
						ref={activePageRef}
						sx={{
							boxSizing: 'border-box',
							width: '100%',
						}}
					>
						{pages[currentPage]?.content}
					</Box>
				}
			</Box>
			<Stack direction='row' justifyContent={'end'} gap={Dimens.spacingMd} mt={Dimens.spacing3xl}>
				<Fade in={!isFirstPage} appear={false}>
					{/* This box is here because fade doesn't seem to work nicely with Action button. */}
					<Box>
						<ActionButton
							label={abortButtonText}
							style={ActionButtonStyle.Bordered}
							loading={isLoading}
							onClick={() => {
								goTo(currentPage - 1);
							}}
							sx={{ width: Dimens.spacing12xl }}
						/>
					</Box>
				</Fade>
				<ActionButton
					label={confirmButtonText}
					variant={StatusVariant.Brand}
					loading={isLoading}
					onClick={() => {
						goTo(currentPage + 1);
					}}
					sx={{ width: Dimens.spacing12xl }}
				/>
			</Stack>
		</Box>
	);
};

export default Pager;
